import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { IGoal } from "@gtmhub/goals/models";
import { localize } from "@webapp/localization/utils/localization.utils";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { cancelButton, okButton } from "@webapp/ui/modal/utils/modal.utils";
import { CloneKrModalComponent } from "./clone-okrs/modals/clone-kr-modal/clone-kr-modal.component";
import { CloneOkrModalComponent } from "./clone-okrs/modals/clone-okr-modal/clone-okr-modal.component";
import { InvokerType, MethodType } from "./clone-or-move.types";
import { ConfirmCloneOrMoveComponent, ConfirmCloneOrMoveComponentData } from "./confirm-clone-or-move/confirm-clone-or-move.component";
import { MoveOkrModalComponent, MoveOkrModalData } from "./move-okrs/move-okr-modal.component";

@Injectable({
  providedIn: "root",
})
export class CloneMoveOkrModalService {
  public constructor(private modalService: UiModalService) {}

  public openCloneOkrModal(goal: IGoal): void {
    this.modalService.create<CloneOkrModalComponent, { goal: IGoal }>({
      uiTitle: localize("clone_okr"),
      uiContent: CloneOkrModalComponent,
      uiData: {
        goal: goal,
      },
      uiCentered: true,
      uiFooter: [
        okButton(localize("clone"), {
          onClick: (component) => {
            component.startCloning();
            return false;
          },
          testId: "button-clone-key-result",
          disabled: (component) => !component.selectedModalOptions.session?.id,
        }),
        cancelButton(localize("cancel")),
      ],
    });
  }

  public openCloneKrModal(metric: Metric): void {
    this.modalService.create<CloneKrModalComponent, { metric: Metric }>({
      uiTitle: localize("clone_key_result"),
      uiContent: CloneKrModalComponent,
      uiData: {
        metric: metric,
      },
      uiCentered: true,
      uiFooter: [
        okButton(localize("clone"), {
          onClick: (component) => {
            component.startCloning();
            return false;
          },
          testId: "button-clone-key-result",
          disabled: (component) => !component.selectedModalOptions.session?.id,
        }),
        cancelButton(localize("cancel")),
      ],
    });
  }

  public openMoveOkrModal(invokerType: InvokerType, goal: IGoal, metric?: Metric): void {
    this.modalService.create<MoveOkrModalComponent, MoveOkrModalData>({
      uiTitle: `${invokerType === "okr" ? localize("move_okr") : localize("move_key_result")}`,
      uiContent: MoveOkrModalComponent,
      uiData: {
        invokerType: invokerType,
        goal: goal,
        metric: metric,
      },
      uiCentered: true,
      uiFooter: [
        okButton(localize("move"), {
          onClick: (component) => {
            component.prepareToMove();
            return false;
          },
          disabled: (component) => !component.selectedModalOptions.session?.id,
        }),
        cancelButton(localize("cancel")),
      ],
    });
  }

  public openConfirmCloneOrMoveModal(method: MethodType, customFieldsCount: number, invokerType: InvokerType): Subject<boolean> {
    const confirmCloneOrMoveModal = this.modalService.create<ConfirmCloneOrMoveComponent, ConfirmCloneOrMoveComponentData>({
      uiContent: ConfirmCloneOrMoveComponent,
      uiData: {
        method,
        customFieldsCount,
        invokerType,
      },
      uiCentered: true,
      uiFooter: [
        okButton(method === "clone" ? localize("clone") : localize("move"), {
          onClick: (component) => component.confirm(),
        }),
        cancelButton(localize("cancel"), {
          onClick: (component) => component.destroyModal(),
        }),
      ],
    });

    return confirmCloneOrMoveModal.afterClose;
  }
}
